import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import EmailInput from '../components/analyzer/EmailInput';
import { api } from '../api/client';

const SOURCES = [
  { name: 'SPF / DKIM / DMARC', desc: 'Email authentication' },
  { name: 'SpamHaus', desc: 'IP & domain blocklists' },
  { name: 'AbuseIPDB', desc: 'IP reputation' },
  { name: 'VirusTotal', desc: 'URL analysis' },
  { name: 'PhishTank', desc: 'Phishing database' },
  { name: 'Google Safe Browsing', desc: 'Threat lists' },
  { name: 'WHOIS', desc: 'Domain registration' },
  { name: 'DNS Analysis', desc: 'MX/SPF/DMARC records' },
  { name: 'Content Analysis', desc: 'Pattern detection' },
  { name: 'Modus Operandi DB', desc: '15+ scam patterns' },
];

function AnimatedCounter({ target, suffix = '', duration = 2000 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          const startTime = Date.now();
          const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * target));
            if (progress < 1) requestAnimationFrame(animate);
          };
          requestAnimationFrame(animate);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target, duration]);

  return <span ref={ref}>{count.toLocaleString()}{suffix}</span>;
}

export default function HomePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({ total_analyses: 0, scams_detected: 0 });
  const [recentAnalyses, setRecentAnalyses] = useState([]);

  useEffect(() => {
    api.getStats().then(res => setStats(res.data)).catch(() => {});
    api.getHistory({ limit: 5 }).then(res => {
      setRecentAnalyses(res.data?.results || res.data || []);
    }).catch(() => {});
  }, []);

  const handleSubmit = async (data) => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await api.submitEmail(data);
      const { analysis_id, identifier } = response.data;
      navigate(`/scan/${analysis_id}`, {
        state: { analysisId: analysis_id, identifier: identifier || analysis_id },
      });
    } catch (err) {
      setError(err.message || 'Failed to submit email');
      setIsLoading(false);
    }
  };

  const getVerdictStyle = (verdict) => {
    const map = {
      legitimate: 'badge-safe',
      caution: 'badge-caution',
      suspicious: 'badge-suspicious',
      scam: 'badge-danger',
    };
    return map[verdict] || 'badge-safe';
  };

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="relative overflow-hidden py-20 md:py-28">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-b from-safe/5 via-transparent to-transparent" />

        <div className="relative max-w-5xl mx-auto px-4 text-center">
          <div className="inline-flex items-center gap-2 bg-safe/10 border border-safe/20 rounded-full px-4 py-1.5 mb-8 text-safe text-sm font-medium">
            <span className="w-2 h-2 bg-safe rounded-full animate-pulse" />
            {t('home.badge')}
          </div>

          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            {t('home.title')}{' '}
            <span className="text-gradient">{t('home.titleHighlight')}</span>
          </h1>

          <p className="text-gray-400 text-lg md:text-xl max-w-2xl mx-auto mb-12">
            {t('home.subtitle')}
          </p>

          <EmailInput onSubmit={handleSubmit} isLoading={isLoading} />

          {error && (
            <p className="text-danger text-sm mt-4">{error}</p>
          )}
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 border-y border-dark-border">
        <div className="max-w-5xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-white mb-1">
                <AnimatedCounter target={stats.total_analyses || 0} suffix="+" />
              </div>
              <div className="text-gray-400">{t('home.statsAnalyzed')}</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-danger mb-1">
                <AnimatedCounter target={stats.scams_detected || 0} suffix="+" />
              </div>
              <div className="text-gray-400">{t('home.statsScams')}</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-safe mb-1">16</div>
              <div className="text-gray-400">{t('home.statsSources')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="section-heading">{t('home.howItWorks')}</h2>
            <p className="section-subheading">{t('home.howItWorksSubtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { n: '1', title: t('home.step1Title'), desc: t('home.step1Desc') },
              { n: '2', title: t('home.step2Title'), desc: t('home.step2Desc') },
              { n: '3', title: t('home.step3Title'), desc: t('home.step3Desc') },
            ].map(step => (
              <div key={step.n} className="glass-card-hover p-6 text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-safe/10 border border-safe/20 flex items-center justify-center text-safe text-xl font-bold">
                  {step.n}
                </div>
                <h3 className="text-white font-semibold text-lg mb-2">{step.title}</h3>
                <p className="text-gray-400 text-sm">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Analyses */}
      {recentAnalyses.length > 0 && (
        <section className="py-16 border-t border-dark-border">
          <div className="max-w-5xl mx-auto px-4">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="section-heading">{t('home.recentAnalyses')}</h2>
                <p className="text-gray-400">{t('home.recentSubtitle')}</p>
              </div>
              <Link to="/history" className="btn-secondary text-sm">{t('home.viewAll')}</Link>
            </div>

            <div className="space-y-3">
              {recentAnalyses.map((a, i) => (
                <Link
                  key={a.id || i}
                  to={`/result/${encodeURIComponent(a.sender_email || a.sender_phone || a.sender_pseudo || a.id)}`}
                  className="glass-card-hover p-4 flex items-center justify-between block"
                >
                  <div>
                    <div className="text-white font-mono text-sm">{a.sender_email || a.sender_domain || 'Unknown'}</div>
                    <div className="text-gray-500 text-xs truncate max-w-md">{a.subject}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-sm text-gray-300">{a.risk_score}/100</span>
                    <span className={getVerdictStyle(a.verdict)}>{a.verdict}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Sources */}
      <section className="py-20 border-t border-dark-border">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="section-heading">{t('home.sources')}</h2>
            <p className="section-subheading">{t('home.sourcesSubtitle')}</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            {SOURCES.map(s => (
              <div key={s.name} className="glass-card p-4 text-center hover:border-safe/20 transition-all group">
                <div className="text-white text-sm font-medium">{s.name}</div>
                <div className="text-gray-500 text-xs mt-1">{s.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Internal links for SEO */}
      <section className="py-16 border-t border-dark-border">
        <div className="max-w-5xl mx-auto px-4">
          <div className="text-center mb-10">
            <h2 className="section-heading">{t('home.resources')}</h2>
            <p className="section-subheading">{t('home.resourcesSubtitle')}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Link to="/community" className="glass-card-hover p-6 block">
              <h3 className="text-white font-semibold text-lg mb-2">{t('home.communityCard')}</h3>
              <p className="text-gray-400 text-sm">{t('home.communityCardDesc')}</p>
            </Link>
            <Link to="/history" className="glass-card-hover p-6 block">
              <h3 className="text-white font-semibold text-lg mb-2">{t('home.historyCard')}</h3>
              <p className="text-gray-400 text-sm">{t('home.historyCardDesc')}</p>
            </Link>
            <Link to="/about" className="glass-card-hover p-6 block">
              <h3 className="text-white font-semibold text-lg mb-2">{t('home.aboutCard')}</h3>
              <p className="text-gray-400 text-sm">{t('home.aboutCardDesc')}</p>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
